/**
 * Schedule a video meeting — or edit one that has not finished: what, when,
 * for how long, with whom. Colleagues are picked from the team; outside people
 * are typed as email addresses and get the invitation with the calendar card.
 * Client and project are optional and matter after the call: a meeting linked
 * to a project turns its action items into tasks on that project.
 *
 * Pass `initial` to edit instead of create. The guest list it hands back is the
 * whole list, not a delta, so removing a chip removes the invitation.
 */
import { ArrowLeft, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClients } from '../../hooks/useClients';
import { useProjects } from '../../hooks/useProjects';
import { useUsers } from '../../hooks/useUsers';
import type { CreateMeetingInput } from '../../lib/meetApi';
import { useAuthStore } from '../../store/useAuthStore';
import { DateField } from '../ui/DateField';
import { Select } from '../ui/Select';
import { TimeField } from '../ui/TimeField';

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const labelCls = 'mb-1 block text-xs font-medium text-gray-600';

const DURATIONS = [15, 30, 45, 60, 90, 120];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** The next round half hour, as the form's default. */
function defaultSlot(): { date: string; time: string } {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + (30 - (d.getMinutes() % 30)));
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The same shape the form submits, for prefilling it when editing. */
export interface MeetingFormInitial {
  title: string;
  scheduledAt: string;
  durationMinutes: number;
  inviteUserIds: string[];
  inviteEmails: string[];
  clientId: string | null;
  projectId: string | null;
}

/** An ISO instant as the two fields the form edits, in the viewer's own clock. */
function slotFrom(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export function ScheduleMeetingModal({
  initial,
  canReschedule = true,
  hostIsMe = true,
  submitting,
  onClose,
  onSubmit,
}: {
  /**
   * Whether the signed-in person hosts this meeting (always true when
   * creating). Their own address is never a guest: the server drops it, and
   * the host gets the meeting in their own calendar instead.
   */
  hostIsMe?: boolean;
  /** Present when editing an existing meeting; absent when creating one. */
  initial?: MeetingFormInitial;
  /** False for a meeting already running: it cannot be moved, only renamed. */
  canReschedule?: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: CreateMeetingInput) => void | Promise<void>;
}) {
  const { t } = useI18n();
  const me = useAuthStore((s) => s.user);
  const slot = useMemo(() => (initial ? slotFrom(initial.scheduledAt) : defaultSlot()), [initial]);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [date, setDate] = useState(slot.date);
  const [time, setTime] = useState(slot.time);
  const [duration, setDuration] = useState(String(initial?.durationMinutes ?? 60));
  const [userIds, setUserIds] = useState<string[]>(initial?.inviteUserIds ?? []);
  const [emails, setEmails] = useState<string[]>(initial?.inviteEmails ?? []);
  const [emailDraft, setEmailDraft] = useState('');
  const [clientId, setClientId] = useState(initial?.clientId ?? '');
  const [projectId, setProjectId] = useState(initial?.projectId ?? '');
  const [error, setError] = useState<string | null>(null);

  const { data: users } = useUsers();
  const { data: clientsData } = useClients({ sort: 'name', limit: 100, active: 'true' });
  const { data: projectsData } = useProjects({ sort: 'name', limit: 100 });

  const userOptions = useMemo(
    () => (users ?? []).filter((u) => u.id !== me?.id).map((u) => ({ value: u.id, label: u.name })),
    [users, me?.id],
  );
  const clientOptions = (clientsData?.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const addEmail = () => {
    const value = emailDraft.trim().toLowerCase();
    if (!value) return;
    if (!EMAIL_RE.test(value)) {
      setError(t('meet.form.invalidEmail'));
      return;
    }
    if (hostIsMe && me?.email && value === me.email.toLowerCase()) {
      setError(t('meet.form.hostEmail'));
      setEmailDraft('');
      return;
    }
    setError(null);
    if (!emails.includes(value)) setEmails([...emails, value]);
    setEmailDraft('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError(t('meet.form.titleRequired'));
      return;
    }
    if (!date || !time) {
      setError(t('meet.form.whenRequired'));
      return;
    }
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);
    const scheduledAt = new Date(y!, m! - 1, d!, hh!, mm!, 0, 0);
    if (Number.isNaN(scheduledAt.getTime())) {
      setError(t('meet.form.whenRequired'));
      return;
    }
    // A pending address the person typed but did not confirm still counts.
    const pending = emailDraft.trim().toLowerCase();
    const allEmails =
      pending && EMAIL_RE.test(pending) && !emails.includes(pending)
        ? [...emails, pending]
        : emails;
    try {
      await onSubmit({
        title: title.trim(),
        scheduledAt: scheduledAt.toISOString(),
        durationMinutes: Number(duration),
        inviteUserIds: userIds,
        inviteEmails: allEmails,
        clientId: clientId || null,
        projectId: projectId || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* A sheet that fills the phone (with a back arrow up top), a centred
          card on anything wider. */}
      <div className="flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-white shadow-xl sm:h-auto sm:max-h-[92vh] sm:rounded-lg">
        <form onSubmit={(e) => void handleSubmit(e)} className="flex min-h-full flex-col">
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] sm:px-5 sm:pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 sm:hidden"
              aria-label={t('common.close')}
            >
              <ArrowLeft size={20} />
            </button>
            <h2 className="flex-1 text-lg font-semibold text-gray-900">
              {initial ? t('meet.form.editTitle') : t('meet.schedule')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="hidden text-gray-400 hover:text-gray-600 sm:block"
              aria-label={t('common.close')}
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div>
              <label className={labelCls}>{t('meet.form.title')}</label>
              <input
                className={inputCls}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('meet.form.titlePlaceholder')}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div>
                <label className={labelCls}>{t('meet.form.date')}</label>
                <DateField value={date} onChange={setDate} disabled={!canReschedule} />
              </div>
              <div>
                <label className={labelCls}>{t('meet.form.time')}</label>
                <TimeField value={time} onChange={setTime} disabled={!canReschedule} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Select
                  label={t('meet.form.duration')}
                  value={duration}
                  onChange={setDuration}
                  options={DURATIONS.map((m) => ({ value: String(m), label: `${m} min` }))}
                  searchable={false}
                  disabled={!canReschedule}
                />
              </div>
            </div>
            {!canReschedule && (
              <p className="-mt-2 text-xs text-gray-500">{t('meet.form.liveNoReschedule')}</p>
            )}

            {/* Toggle chips, not the filter MultiSelect: there "[] = everyone"
                and selecting all collapses to [], which for invitations would
                mean inviting nobody. */}
            <div>
              <label className={labelCls}>{t('meet.form.colleagues')}</label>
              {userOptions.length === 0 ? (
                <p className="text-sm text-gray-500">{t('meet.form.noColleagues')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {userOptions.map((u) => {
                    const on = userIds.includes(u.value);
                    return (
                      <button
                        key={u.value}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setUserIds(
                            on ? userIds.filter((id) => id !== u.value) : [...userIds, u.value],
                          )
                        }
                        className={[
                          'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                          on
                            ? 'border-brand-600 bg-brand-600 text-fixed-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100',
                        ].join(' ')}
                      >
                        {u.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className={labelCls}>{t('meet.form.guests')}</label>
              <div className="flex flex-wrap gap-1.5 rounded border border-gray-300 px-2 py-1.5 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
                {emails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => setEmails(emails.filter((e) => e !== email))}
                      aria-label={t('common.delete')}
                      className="text-gray-400 hover:text-gray-700"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  className="min-w-40 flex-1 border-0 bg-transparent px-1 py-0.5 text-sm focus:outline-none"
                  type="email"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onBlur={addEmail}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                      e.preventDefault();
                      addEmail();
                    }
                  }}
                  placeholder={t('meet.form.guestsPlaceholder')}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('meet.form.guestsHint')}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label={t('meet.form.client')}
                value={clientId}
                onChange={setClientId}
                options={clientOptions}
                placeholder={t('meet.form.none')}
                clearable
              />
              <Select
                label={t('meet.form.project')}
                value={projectId}
                onChange={setProjectId}
                options={projectOptions}
                placeholder={t('meet.form.none')}
                clearable
              />
            </div>
            <p className="text-xs text-gray-500">{t('meet.form.projectHint')}</p>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="mt-auto flex justify-end gap-2 border-t border-gray-200 px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-50"
            >
              {initial ? t('common.save') : t('meet.form.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
